import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardDocumentListIcon,
  ClockIcon,
  PencilIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getPortalIntakeForms } from '../../services/portalService';
import EmptyState from '../../components/common/EmptyState';
import SkeletonBlock from '../../components/common/SkeletonBlock';

function FormStatusBadge({ response }) {
  if (response.submittedAt) {
    return (
      <span className="inline-flex items-center gap-1 badge badge-success">
        <CheckCircleIcon className="w-3 h-3" />
        Completed
      </span>
    );
  }
  if (response.startedAt) {
    return (
      <span className="inline-flex items-center gap-1 badge badge-info">
        <PencilIcon className="w-3 h-3" />
        In progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 badge badge-warning">
      <ClockIcon className="w-3 h-3" />
      Not started
    </span>
  );
}

export default function PortalIntakeForms() {
  const { data: responses, isLoading, isError } = useQuery({
    queryKey: ['portal', 'intakeForms'],
    queryFn: getPortalIntakeForms,
  });

  return (
    <div className="space-y-6">
      <h1>Intake Forms</h1>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card flex items-center justify-between gap-4">
              <div className="space-y-2 flex-1">
                <SkeletonBlock className="h-4 w-40" rounded="rounded" />
                <SkeletonBlock className="h-3 w-24" rounded="rounded-full" />
              </div>
              <SkeletonBlock className="h-8 w-24" rounded="rounded-lg" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">
          Failed to load forms.
        </div>
      ) : !responses?.length ? (
        <EmptyState
          icon={ClipboardDocumentListIcon}
          title="No pending intake forms"
          description="Your provider will send forms here when needed."
        />
      ) : (
        <div className="space-y-3">
          {responses.map((r) => {
            const isCompleted = !!r.submittedAt;
            return (
              <div
                key={r.id}
                className={`card flex items-center justify-between gap-4 ${
                  isCompleted ? 'opacity-70' : ''
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    isCompleted
                      ? 'bg-success-100 dark:bg-success-900/30'
                      : 'bg-teal-100 dark:bg-teal-900/30'
                  }`}>
                    <ClipboardDocumentListIcon className={`w-5 h-5 ${
                      isCompleted
                        ? 'text-success-600 dark:text-success-400'
                        : 'text-teal-600 dark:text-teal-400'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-900 dark:text-neutral-100">
                      {r.form?.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <FormStatusBadge response={r} />
                      {r.submittedAt && (
                        <span className="text-xs text-neutral-400 dark:text-neutral-500">
                          {format(new Date(r.submittedAt), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!isCompleted ? (
                  <Link
                    to={`/portal/intake-forms/${r.id}`}
                    className="btn btn-sm btn-primary shrink-0"
                  >
                    {r.startedAt ? 'Continue' : 'Start form'}
                  </Link>
                ) : (
                  <Link
                    to={`/portal/intake-forms/${r.id}`}
                    className="btn btn-sm btn-secondary shrink-0"
                  >
                    View
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
